import moment from "moment";
import React from "react";
import type Stripe from "stripe";
import styled from "styled-components";

import { WorkspacePlanTier } from "common/interfaces";
import { Button } from "js/Components/Button";
import BillingController, { BillingControllerState } from "js/controllers/BillingController";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import getLogger, { LogGroup } from "js/core/logger";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import { themeColors } from "js/react/sharedStyles";
import { ChangePaymentMethodDialog } from "js/react/views/UserOptions/Billing/ChangePaymentMethodDialog";
import { UpdateBillingDetailsDialog } from "js/react/views/UserOptions/Billing/UpdateBillingDetailsDialog";

import { CreditCardIcon } from "./CreditCardIcon";
import { BlockContainer, BlockContentContainer, BlockHeader } from "./Containers";
import { PromoCoupon, PromoForm } from "../../../components/PromoForm";
import { LinkByStripeIcon } from "./LinkByStripeIcon";

export const logger = getLogger(LogGroup.BILLING);

const PaymentMethodContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    text-transform: capitalize;
    color: #666;
    font-size: 15px;
`;

const PaymentMethodInfoContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
`;

const Divider = styled.div`
    width: 100%;
    height: 1px;
    background-color: #eee;
    margin-bottom: 4px;
`;

const BillingDetailsContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
`;

const BillingDetailsInfo = styled.div`
    font-size: 14px;
    display: flex;
    flex-direction: column;
    gap: 5px;
    color: #666;
    align-items: flex-start;
`;

const NextBillInfoContainer = styled.span`
    width: 100%;
    font-size: 14px;
`;

const LinkByStripeAccountContainer = styled.div`
    text-transform: none;
`;

function Link(props: { link: Stripe.PaymentMethod.Link }) {
    const { link } = props;

    const handleEditPaymentMethod = () => {
        ShowDialog(ChangePaymentMethodDialog);
    };

    return (<>
        <PaymentMethodInfoContainer>
            {link
                ? <>
                    <LinkByStripeIcon/>
                    <LinkByStripeAccountContainer data-test-id="payment-method-link-info">{link.email} </LinkByStripeAccountContainer>
                </>
                : <div data-test-id="payment-method-link-info">Link payment data missing</div>
            }
        </PaymentMethodInfoContainer>
        <Button unframed large color={themeColors.ui_blue} onClick={handleEditPaymentMethod} data-test-id="edit-payment-method-button">{link ? "edit" : "add"}</Button>
    </>);
}

function Card(props: { card: Stripe.PaymentMethod.Card }) {
    const { card } = props;

    const handleEditPaymentMethod = () => {
        ShowDialog(ChangePaymentMethodDialog);
    };

    return (<>
        <PaymentMethodInfoContainer>
            {card
                ? <>
                    <CreditCardIcon card={card} />
                    <div data-test-id="payment-method-card-info">{card.brand} ending in {card.last4}</div>
                </>
                : <div data-test-id="payment-method-card-info">Credit card data missing</div>
            }
        </PaymentMethodInfoContainer>
        <Button unframed large color={themeColors.ui_blue} onClick={handleEditPaymentMethod} data-test-id="edit-payment-method-button">{card ? "edit" : "add"}</Button>
    </>);
}

const NextBillInfo = WorkspaceController.withInitializedState(BillingController.withInitializedState(function NextBillInfo(props: BillingControllerState & WorkspaceControllerState) {
    const { stripeData: { upcomingInvoice, subscription }, plan } = props;

    const nextBillDate = moment.unix(subscription.current_period_end).format("MMM DD YYYY");

    if (subscription.status === "trialing") {
        if (subscription.cancel_at_period_end) {
            if (plan.tier === WorkspacePlanTier.PRO) {
                return (<NextBillInfoContainer>
                    Your subscription has been canceled. You will be downgraded to the Basic (free)
                    plan when your trial ends on {nextBillDate}.
                </NextBillInfoContainer>);
            }

            return (<NextBillInfoContainer>Your trial has been canceled.</NextBillInfoContainer>);
        }

        if (upcomingInvoice) {
            return (<NextBillInfoContainer>
                Your first {subscription.scheduled_billing_interval}ly bill of <b>${(upcomingInvoice.amount_due / 100).toFixed(2)}</b> will be due when your free trial ends on <b>{nextBillDate}</b>.
            </NextBillInfoContainer>);
        }

        return null;
    }

    if (subscription.cancel_at_period_end) {
        if (plan.tier === WorkspacePlanTier.PRO) {
            return (<NextBillInfoContainer>
                Your subscription has been canceled. You will be downgraded to the Basic (free)
                plan at the end of your current billing period on <b>{nextBillDate}</b>.
            </NextBillInfoContainer>);
        }

        return (<NextBillInfoContainer>Your plan has been canceled.</NextBillInfoContainer>);
    }

    if (upcomingInvoice) {
        return (<NextBillInfoContainer>
            Your next {subscription.scheduled_billing_interval}ly bill of <b>${(upcomingInvoice.amount_due / 100).toFixed(2)}</b> is due on <b>{nextBillDate}</b>.
        </NextBillInfoContainer>);
    }

    return null;
}));

const PastDueMessage = styled.div`
    font-size: 14px;
    margin-top: 5px;
    color: ${themeColors.warning};
    font-weight: 600;
    width: 100%;
    text-align: left;
`;

const PromoBlock = BillingController.withInitializedState(function PromoBlock(props: BillingControllerState) {
    const { stripeData: { upcomingInvoice } } = props;

    const verifyPromoCode = async (promoCode: Stripe.PromotionCode) => {
        await BillingController.applyPromotionCodeToSubscription(promoCode?.code);
    };

    // Verify the first discount with a coupon (there should be only one)
    const firstDiscount = upcomingInvoice?.discounts?.find((discount: Stripe.Discount) => discount?.coupon);

    if (firstDiscount && (typeof firstDiscount === "object")) {
        return (<PromoCoupon coupon={firstDiscount.coupon}/>);
    }

    return (<PromoForm verifyPromoCode={verifyPromoCode} />);
});

export const PaymentMethodBlock = WorkspaceController.withInitializedState(BillingController.withInitializedState(
    function PaymentMethodBlock(props: WorkspaceControllerState & BillingControllerState) {
        const { stripeData: { subscription, paymentMethods, customer }, plan } = props;

        if (!plan.requiresSubscription) {
            return null;
        }

        const paymentMethod = subscription.default_payment_method ?? Object.values(paymentMethods)[0] ?? null;

        const address = customer.address ?? paymentMethod?.billing_details.address ?? null;

        const handleEditBillingDetails = () => {
            ShowDialog(UpdateBillingDetailsDialog);
        };

        return (<BlockContainer data-test-id="payment-method-block">
            <BlockContentContainer top dense>
                {plan.tier !== WorkspacePlanTier.ENTERPRISE && <>
                    <BlockHeader data-test-id="payment-method-block-header">Payment Method</BlockHeader>
                    <PaymentMethodContainer>
                        {paymentMethod.type === "link"
                            ? <Link link={paymentMethod?.link} />
                            : <Card card={paymentMethod?.card} />}
                    </PaymentMethodContainer>
                    <Divider />
                </>}
                <BlockHeader data-test-id="billing-details-block-header">Billing Details</BlockHeader>
                <BillingDetailsContainer>
                    <BillingDetailsInfo>
                        {customer.name && <div>{customer.name}</div>}
                        {customer.email && <div>{customer.email}</div>}
                        {address && <div>{[address.line1, address.line2, address.city, address.state, address.postal_code, address.country].filter(Boolean).join(", ")}</div>}
                    </BillingDetailsInfo>
                    <Button unframed large color={themeColors.ui_blue} onClick={handleEditBillingDetails}>edit</Button>
                </BillingDetailsContainer>
                <Divider />
                <NextBillInfo />
                {plan.tier !== WorkspacePlanTier.ENTERPRISE && <PromoBlock />}
                {subscription.status === "past_due" && <PastDueMessage>Past due</PastDueMessage>}
            </BlockContentContainer>
        </BlockContainer>);
    }
));
